<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import Calendar from 'lucide-svelte/icons/calendar';
import MapPin from 'lucide-svelte/icons/map-pin';
import Timer from 'lucide-svelte/icons/timer';
import { CUSTOM_COMPONENTS } from '../../CustomComponents.js';
import { BUILDER_PUBLIC_API_KEY } from '../../config/apiKeys.js';
import { ERoutes } from '../../config/enums.js';
import CardGridItem from '../../stories/atoms/CardGridItem/CardGridItem.svelte';
import CardGrid from '../../stories/molecules/CardGrid/CardGrid.svelte';
import EventFilterNav from '../../stories/molecules/EventFilterNav/EventFilterNav.svelte';

interface EventItem {
    id: string;
    data?: {
        date?: string;
        slug?: string;
        title?: string;
        thumbnail?: string;
        duration?: string;
        location?: string;
        blurb?: string;
    };
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

// Filter state - defaults to 'all'
let currentFilter = $state('all');

// shortcut to the actual content
let pageContent = $derived(data.builderProps?.content?.data || {});

// we want to show unpublished content when in preview mode - or just show events if no builder content
let canShowContent = $derived(pageContent || isPreviewing() || true);

// Get ordinal suffix for day
function getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}

// Format date for display
function formatDate(dateString: string): string {
    const date = new Date(dateString);

    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();

    // Format time as 12-hour with lowercase am/pm
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

    return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month} ${year} at ${hours}:${minutesStr}${ampm}`;
}

// Click handlers for filter navigation
const onAllClick = () => {
    currentFilter = 'all';
};

const onUpcomingClick = () => {
    currentFilter = 'upcoming';
};

const onPastClick = () => {
    currentFilter = 'past';
};

// Filter links with active states
let filterLinks = $derived([
    {
        key: 'all',
        text: 'All',
        to: ERoutes.Events,
        active: currentFilter === 'all',
        onClick: onAllClick,
    },
    {
        key: 'upcoming',
        text: 'Upcoming',
        to: ERoutes.Events,
        active: currentFilter === 'upcoming',
        onClick: onUpcomingClick,
    },
    {
        key: 'past',
        text: 'Past',
        to: ERoutes.Events,
        active: currentFilter === 'past',
        onClick: onPastClick,
    },
]);

// Filtered events based on current filter
let filteredEvents = $derived(
    (data.allEvents as EventItem[] | undefined)?.filter((event: EventItem) => {
        if (currentFilter === 'all') {
            return true;
        }

        if (!event.data?.date) return false;
        const eventDate = new Date(event.data.date);
        const now = new Date();

        if (currentFilter === 'upcoming') {
            return eventDate >= now;
        } else if (currentFilter === 'past') {
            return eventDate < now;
        }

        return true;
    }) || [],
);
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key pageContent}
        <!-- Render builder content with all required props (if it exists) -->
        {#if data.builderProps?.content}
            <div class="container container--id-{data?.builderProps?.content?.id}">
                <Content
                    model="page"
                    content={data.builderProps.content}
                    apiKey={BUILDER_PUBLIC_API_KEY}
                    customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
                />
            </div>
        {/if}
        <div class="scroll-content-over-header">
            <EventFilterNav links={filterLinks} />

            <CardGrid hasBorder={false} hasBottomMargin={false}>
                {#snippet children()}
                    {#each filteredEvents as event (event.id)}
                        <CardGridItem
                            class="builder-block"
                            to="{ERoutes.Events}{event.data?.slug ?? ''}"
                            title={event.data?.title ?? ''}
                            image={{
                                src: event.data?.thumbnail,
                                altText: event.data?.title ?? '',
                            }}
                        >
                            {#snippet customDescription()}
                                <div class="event-meta">
                                    {#if event.data?.date}
                                        <div class="event-meta__item">
                                            <Calendar size={16} />
                                            <span>{formatDate(event.data.date)}</span>
                                        </div>
                                    {/if}
                                    {#if event.data?.duration}
                                        <div class="event-meta__item">
                                            <Timer size={16} />
                                            <span>{event.data.duration}</span>
                                        </div>
                                    {/if}
                                    {#if event.data?.location}
                                        <div class="event-meta__item">
                                            <MapPin size={16} />
                                            <span>{event.data.location}</span>
                                        </div>
                                    {/if}
                                    {#if event.data?.blurb}
                                        <div class="event-blurb">
                                            <p>{event.data.blurb}</p>
                                        </div>
                                    {/if}
                                </div>
                            {/snippet}
                        </CardGridItem>
                    {/each}
                {/snippet}
            </CardGrid>

            {#if !filteredEvents || filteredEvents.length === 0}
                <div class="no-events-message">
                    <p>No events available for this filter. Please try another option!</p>
                </div>
            {/if}
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .scroll-content-over-header {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .no-events-message {
        padding: var(--layout-pagegrid-margin);
        text-align: center;

        p {
            color: var(--colour-text-secondary, #666);
            font-size: 1.125rem;
            margin: 0;
        }
    }

    .container
        > :global(
            div
                > .builder-blocks
                > .builder-block:not(> .section-with-background)
        ) {
        padding-bottom: var(--layout-pagegrid-margin);
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);
        padding-top: var(--layout-pagegrid-padding-top);
    }

    :global(.event-meta) {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
    }

    :global(.event-meta__item) {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        color: var(--colour-squant);

        :global(svg) {
            flex-shrink: 0;
        }

        span {
            line-height: 1.4;
        }
    }

    :global(.event-blurb) {
        margin-top: 0.8rem;

        p {
            margin: 0;
            color: var(--colour-squant);
            line-height: 1.5;
        }
    }
</style>
